import { Component, computed, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { Package } from '../../../models/package';
import {
    selectShipmentEditorPackages,
    selectShipmentEditorShipment,
    shipmentEditorBoxPackingBusy
} from '../../../store/shipment-editor/shipment-editor.state';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    shipmentEditorAddEmptyPackage,
    shipmentEditorAddPackage, shipmentEditorBoxPack,
    shipmentEditorDeletePackages,
    shipmentEditorPatchPackages
} from '../../../store/shipment-editor/shipment-editor.actions';
import { MatButton } from '@angular/material/button';
import { matchChoices } from '../../../utils/match-choices';
import { PackagingTypeEnum } from '../../../enum/packaging-type.enum';
import { Shipment } from '../../../models/shipment';
import { freightClasses } from '../../../list/freight-classes';
import { ShipmentOption } from '../../../enum/shipment-option.enum';
import { hazmatClasses } from '../../../list/hazmat-classes';
import { packagingUnits } from '../../../list/packaging-units';
import { DigitsPipe } from '../../shared/pipes/digits.pipe';
import { freightClass } from '../../../utils/freight-class';
import { WeightUnitEnum } from '../../../enum/weight-unit.enum';
import { DimensionUnitEnum } from '../../../enum/dimension-unit.enum';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { selectProfileBoxPackingTemplates } from '../../../store/profile/profile.state';

@Component({
    selector: 'app-shipment-packages-table',
    standalone: true,
    templateUrl: './shipment-packages-table.component.html',
    styleUrls: [ './shipment-packages-table.component.scss' ],
    imports: [
        MatTable, MatColumnDef, MatHeaderCell, MatHeaderCellDef,
        MatCheckbox, MatCell, MatCellDef,
        MatHeaderRow, MatRow, MatHeaderRowDef, MatRowDef, MatInput, MatOption, MatAutocomplete, MatAutocompleteTrigger, DecimalPipe,
        MatButton, CurrencyPipe, DigitsPipe, MatProgressSpinner
    ]
})
export class ShipmentPackagesTableComponent {

    displayedColumns = [
        'select', 'quantity', 'description', 'packageCode', 'stackable',
        'length', 'width', 'height', 'weight', 'value'
    ];

    smallParcelColumns = [
        'select', 'quantity', 'description',
        'length', 'width', 'height', 'weight'
    ];
    smallParcelColumnsInsured = [ ...this.smallParcelColumns, 'value' ];
    ltlColumns = [
        'select', 'quantity', 'description', 'packageCode', 'stackable',
        'length', 'width', 'height', 'weight', 'value'
    ];
    ltlUsaColumns = [ 'select', 'quantity', 'description', 'freightClass', 'nmfcNumber', 'packageCode', 'stackable',
        'length', 'width', 'height', 'weight', 'value' ];
    freightClasses = freightClasses;
    hazmatClasses = hazmatClasses;
    packageCodeChoices = packagingUnits;

    selectedIds = new Set<number>();

    constructor(private store: Store<AppState>) {
    }

    public packages = toSignal(this.store.select(selectShipmentEditorPackages));
    public currency = toSignal(this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => shipment.currency)
    ), { initialValue: 'CAD' });
    public weightUnit: Signal<WeightUnitEnum> = toSignal(this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => shipment.weightUnit === 'KG' ? WeightUnitEnum.KG : WeightUnitEnum.LB)
    ), { initialValue: WeightUnitEnum.KG });
    public dimensionUnit: Signal<DimensionUnitEnum> = toSignal(this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => shipment.dimensionUnit === 'CM' ? DimensionUnitEnum.CM : DimensionUnitEnum.IN)
    ), { initialValue: DimensionUnitEnum.CM });
    public weightUnitDisplay = computed(() => this.weightUnit() === 'KG' ? $localize`KGs` : $localize`LBs`);
    public dimensionUnitDisplay = computed(() => this.dimensionUnit() === 'CM' ? $localize`CM` : $localize`IN`);

    public boxPackingBusy = toSignal(this.store.select(shipmentEditorBoxPackingBusy));
    public packagesBusy = computed(() => this.boxPackingBusy());
    public boxPackingTemplates = toSignal(this.store.select(selectProfileBoxPackingTemplates));
    public boxPackingEnabled = computed(() => this.boxPackingTemplates().length > 0);

    public shipment$ = this.store.select(selectShipmentEditorShipment);

    totalActualWeight = toSignal(this.shipment$.pipe(
        map(s => s.packages.map(p => p.quantity * p.weight)),
        map(p => p.length ? p : [ 0 ]),
        map(p => p.reduce((acc, cur) => acc + cur))
    ), { initialValue: 0 });

    totalDimensionalWeight = toSignal(this.shipment$.pipe(
        map(s => s.packages.map(p => p.quantity * (p.width * p.height * p.length) / (s.weightUnit === 'KG' ? 5000 : 139))),
        map(p => p.length ? p : [ 0 ]),
        map(p => p.reduce((acc, cur) => acc + cur))
    ), { initialValue: 0 });

    dimensionalWeightGreater = computed(() => this.totalDimensionalWeight() > this.totalActualWeight());

    totalInsurance = toSignal(this.shipment$.pipe(
        map(s => s.packages.map(p => p.quantity * p.value)),
        map(p => p.length ? p : [ 0 ]),
        map(p => p.reduce((acc, cur) => +acc + +cur))
    ), { initialValue: 0 });

    displayColumns = toSignal(this.shipment$.pipe(
        map(s => this.columnsToDisplay(s))
    ), { initialValue: this.smallParcelColumns });

    columnsToDisplay(s: Shipment): string[] {
        if (s.packagingType === PackagingTypeEnum.PALLET) {
            let columns = [ ...this.ltlColumns ];
            if (s.shipper.country === 'US' || s.destination.country === 'US') {
                columns = [ ...this.ltlUsaColumns ];
            }
            if (!!s.courierOptions[ShipmentOption.HAZARDOUS_MATERIALS]) {
                columns.push('hazmatClass');
                columns.push('unNumber');
            }
            return columns;
        } else {
            if (s.packages.filter(p => p.value > 0).length > 0) {
                return [ ...this.smallParcelColumnsInsured ];
            }
            return [ ...this.smallParcelColumns ];
        }
    }

    toggleSelection(): void {
        if (this.selectedIds.size === 0) {
            for (let i = 0; i < this.packages().length; i++) {
                this.selectedIds.add(i);
            }
        } else {
            this.selectedIds.clear();
        }
    }

    isAllSelected(): boolean {
        return this.selectedIds.size === this.packages().length && this.selectedIds.size > 0;
    }

    isSelectionIndeterminate(): boolean {
        return this.selectedIds.size > 0 && this.selectedIds.size < this.packages().length;
    }

    toggleSelectRow(i: number): void {
        if (this.selectedIds.has(i)) {
            this.selectedIds.delete(i);
        } else {
            this.selectedIds.add(i);
        }
    }

    isSelected(i: number): boolean {
        return this.selectedIds.has(i);
    }

    doPatch(index: number, field: string, value: any): void {
        const indices = this.selectedIds.size === 0 ? [ index ] : [ ...Array.from(this.selectedIds), index ];
        switch (field) {
            case 'quantity':
                value = Math.max(1, value);
                break;
            case 'weight':
                value = Math.max(0.01, value);
                break;
            case 'width':
            case 'length':
            case 'height':
                value = Math.max(1, value);
                break;
            case 'value':
                value = Math.max(0, value);
                break;
            case 'insurance':
                value = Math.max(0, value);
                break;
            case 'packageCode':
                value = matchChoices(value, this.packageCodeChoices, true);
                break;
            case 'freightClass':
                value = matchChoices(value, this.freightClasses, false);
                break;
            case 'unNumber':
                value = value.replace(/[^0-9]/g, '');
                break;
            case 'nmfcNumber':
                value = value.replace(/[^0-9]/g, '');
                break;
        }
        const pkg: Partial<Package> = { [field]: value };
        let willChange = false;
        for (const i of indices) {
            const p = this.packages()[i];
            if (p[field] !== value) {
                willChange = true;
                break;
            }
        }
        if (willChange) {
            setTimeout(() => this.store.dispatch(shipmentEditorPatchPackages({ indices, pkg })));
        }
    }

    onFieldBlur(index: number, field: string, event: FocusEvent): void {
        const input = event.target as HTMLInputElement;
        this.doPatch(index, field, input.value);
    }

    onCheckbox(index: number, field: string, checked: boolean): void {
        this.doPatch(index, field, checked);
    }

    trackBy(index: number, item: any): number {
        return index;
    }

    addEmptyPackage(): void {
        this.store.dispatch(shipmentEditorAddEmptyPackage());
    }

    addTemplatePackage(): void {
        this.store.dispatch(shipmentEditorAddPackage());
    }

    deletePackages(): void {
        this.store.dispatch(shipmentEditorDeletePackages({ packageIndices: [ ...this.selectedIds ] }));
        this.selectedIds.clear();
    }

    defaultFreightClass(pkg: Package): string {
        return freightClass(pkg, this.weightUnit(), this.dimensionUnit());
    }

    boxPack(): void {
        this.store.dispatch(shipmentEditorBoxPack());
    }
}
